import React, { useState, useEffect, useRef } from "react";
import "./App.css";
import axios from "axios";
import { BrowserRouter, Routes, Route, useNavigate, useLocation, Navigate, Outlet, useOutletContext, Link } from "react-router-dom";

// Import components
import TaskLists from './components/TaskLists';
import UserManagement from './components/UserManagement';
import RoleManagement from './components/RoleManagement';
import DepartmentManagement from './components/DepartmentManagement';
import SiteManagement from './components/SiteManagement';
import SiteAssigner from './components/SiteAssigner';
import TeamDashboardSettings from './components/TeamDashboardSettings';
import TeamCapacity from './components/TeamCapacity';
import WorkDiaryFilters from './components/WorkDiaryFilters';
import { API, fetchUsers as utilFetchUsers, fetchSites as utilFetchSites, fetchRoles as utilFetchRoles, fetchDepartments as utilFetchDepartments } from './utils/api';

// Import authentication components
import AdminLogin from './components/auth/AdminLogin';
import SuperAdminLogin from './components/auth/SuperAdminLogin';
import ProtectedAdminRoute from './components/auth/ProtectedAdminRoute';
import ProtectedSuperAdminRoute from './components/auth/ProtectedSuperAdminRoute';
import MobileTokenAuthHandler from './components/auth/MobileTokenAuthHandler';

// Import landing page
import LandingPage from './pages/LandingPage';
import TourPage from './pages/TourPage';

// Import registration
import OrganisationRegistration from './components/OrganisationRegistration';

// Import password reset pages
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';

// Import legal pages
import TermsOfServicePage from './pages/TermsOfServicePage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';

// Import support pages
import FAQsPage from './pages/FAQsPage';
import AccountDeletionPage from './pages/AccountDeletionPage';

// Import team portal pages
import TeamLoginPage from './pages/team/TeamLoginPage';
import TeamDashboardPage from './pages/team/TeamDashboardPage';

// Import layout components
import AdminLayout from './components/layout/AdminLayout';
import SuperAdminLayout from './components/layout/SuperAdminLayout';

// Import super admin page components
import SuperAdminDashboardPage from './pages/super-admin/SuperAdminDashboardPage';
import OrganisationsPage from './pages/super-admin/OrganisationsPage';
import OrganisationDataPage from './pages/super-admin/OrganisationDataPage';
import OrganisationAnalyticsPage from './pages/super-admin/OrganisationAnalyticsPage';
import ExploreHubPage from './pages/super-admin/ExploreHubPage';
import PayPalSetupPage from './pages/super-admin/PayPalSetupPage';
import SubscriptionPackagesPage from './pages/super-admin/SubscriptionPackagesPage';
import FlashUpgradesPage from './pages/super-admin/FlashUpgradesPage';
import FinancialsPage from './pages/super-admin/FinancialsPage';
import SettingsUsersPage from './pages/super-admin/SettingsUsersPage';
import TicketsPage from './pages/super-admin/TicketsPage';
import BroadcastPage from './pages/super-admin/BroadcastPage';
import ToolsReportsPage from './pages/super-admin/ToolsReportsPage';
import AppMetadataPage from './pages/super-admin/AppMetadataPage';
import FeedbackBugsPage from './pages/super-admin/FeedbackBugsPage';
import NotificationAnalyticsPage from './pages/super-admin/NotificationAnalyticsPage';
import AppAnalyticsPage from './pages/super-admin/AppAnalyticsPage';
import AdminPortalAnalyticsPage from './pages/super-admin/AdminPortalAnalyticsPage';
import UsageFeatureAdoptionPage from './pages/super-admin/UsageFeatureAdoptionPage';
import WorkflowProductivityPage from './pages/super-admin/WorkflowProductivityPage';
import AccessSecurityPage from './pages/super-admin/AccessSecurityPage';
import ErrorSupportPage from './pages/super-admin/ErrorSupportPage';
import UserEngagementPage from './pages/super-admin/UserEngagementPage';
import FeatureUsagePage from './pages/super-admin/FeatureUsagePage';
import UserBehaviourPage from './pages/super-admin/UserBehaviourPage';
import PerformanceTechnicalPage from './pages/super-admin/PerformanceTechnicalPage';
import RetentionLifecyclePage from './pages/super-admin/RetentionLifecyclePage';
import TimeBasedInsightsPage from './pages/super-admin/TimeBasedInsightsPage';
import GeographyDemographicsPage from './pages/super-admin/GeographyDemographicsPage';
import FinancialHealthPage from './pages/super-admin/FinancialHealthPage';
import SubscriptionsPage from './pages/super-admin/SubscriptionsPage';
import NeoSettingsPage from './pages/super-admin/NeoSettingsPage';
import NeoAnalyticsPage from './pages/super-admin/NeoAnalyticsPage';
import NeoHistoryPage from './pages/super-admin/NeoHistoryPage';
import NeoFeedbackPage from './pages/super-admin/NeoFeedbackPage';
import ExploreAnalyticsPage from './pages/super-admin/ExploreAnalyticsPage';
import DeploymentToolsPage from './pages/super-admin/DeploymentToolsPage';
import PayPalEventsPage from './pages/super-admin/PayPalEventsPage';
import PlatformLogsPage from './pages/super-admin/PlatformLogsPage';
import SystemLogsPage from './pages/super-admin/SystemLogsPage';
import MarketingCampaignsPage from './pages/super-admin/MarketingCampaignsPage';
import FAQManagementPage from './pages/super-admin/FAQManagementPage';
import NeoCreditPurchasePage from './pages/admin/NeoCreditPurchasePage';

// Import dashboard components
import DashboardOverviewEnhanced from './components/dashboard/DashboardOverviewEnhanced';

// Import reports component
import Reports from './components/reports/Reports';

// Import admin page components
import AdminExpensesPageComponent from './pages/admin/AdminExpensesPage';
import AdminOvertimePageComponent from './pages/admin/AdminOvertimePage';
import AdminHolidaysPageComponent from './pages/admin/HolidaysPage';
import AdminPaymentsPageComponent from './pages/admin/AdminPaymentsPage';
import AdminInvoicesPageComponent from './pages/admin/AdminInvoicesPage';
import AdminPlansPageComponent from './pages/admin/AdminPlansPage';
import UserPreferencesPage from './pages/admin/UserPreferencesPage';
import AdminProcurementPage from './pages/admin/AdminProcurementPage';
import AdminWorkLogPage from './pages/admin/AdminWorkLogPage';
import AdminAttendanceTaskPage from './pages/admin/AdminAttendanceTaskPage';
import PhotoGalleryPage from './pages/admin/PhotoGalleryPage';
import AdminQuotingPage from './pages/admin/AdminQuotingPage';
import AdminClientsPage from './pages/admin/AdminClientsPage';

// Import PWA utilities
import PWA from './utils/pwa';

// Set up axios defaults
axios.defaults.headers.common['Content-Type'] = 'application/json';

// Initialize PWA features
PWA.registerSW();
// PWA.setupInstallPrompt(); // Disabled - removes "Install SiteOrbit" prompt

// Handle app shortcuts on page load
document.addEventListener('DOMContentLoaded', () => {
  PWA.handleAppShortcuts();
});

// Admin Page Components
const AdminDashboardPage = () => {
  const { users = [], sites = [], fetchData } = useOutletContext();
  const navigate = useNavigate();
  const [subscriptionFeatures, setSubscriptionFeatures] = React.useState(null);
  const [isChecking, setIsChecking] = React.useState(true);

  React.useEffect(() => {
    const checkAccess = async () => {
      try {
        const response = await axios.get(`${API}/admin/organisation`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        const features = response.data.subscription_features || {};
        setSubscriptionFeatures(features);
        
        // If dashboard feature is not available, redirect to plans (safe landing page)
        if (!features.dashboard) {
          navigate('/admin/plans', { replace: true });
        }
      } catch (error) {
        console.error('Failed to check subscription:', error);
      } finally {
        setIsChecking(false);
      }
    };
    
    checkAccess();
  }, [navigate]);

  if (isChecking) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-600"></div>
        <span className="ml-3 text-gray-600">Checking access...</span>
      </div>
    );
  }

  if (subscriptionFeatures && !subscriptionFeatures.dashboard) {
    return null; // Will be redirected
  }

  return <DashboardOverviewEnhanced users={users} sites={sites} fetchUsers={fetchData} fetchSites={fetchData} fetchTasks={fetchData} />;
};

const AdminUsersPage = () => {
  const { users = [], sites = [], fetchData = () => {} } = useOutletContext();
  const navigate = useNavigate();
  const [subscriptionFeatures, setSubscriptionFeatures] = React.useState(null);
  const [isChecking, setIsChecking] = React.useState(true);

  React.useEffect(() => {
    const checkAccess = async () => {
      try {
        const response = await axios.get(`${API}/admin/organisation`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        const features = response.data.subscription_features || {};
        setSubscriptionFeatures(features);
        
        // If teamManagement feature is not available, redirect to plans (safe landing page)
        if (!features.teamManagement) {
          navigate('/admin/plans', { replace: true });
        }
      } catch (error) {
        console.error('Failed to check subscription:', error);
      } finally {
        setIsChecking(false);
      }
    };
    
    checkAccess();
  }, [navigate]);

  if (isChecking) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-600"></div>
        <span className="ml-3 text-gray-600">Checking access...</span>
      </div>
    );
  }

  if (subscriptionFeatures && !subscriptionFeatures.teamManagement) {
    return null; // Will be redirected
  }

  return <UserManagement users={users} sites={sites} fetchUsers={fetchData} />;
};

const AdminTasksPage = () => {
  const navigate = useNavigate();
  const [subscriptionFeatures, setSubscriptionFeatures] = React.useState(null);
  const [isChecking, setIsChecking] = React.useState(true);

  React.useEffect(() => {
    const checkAccess = async () => {
      try {
        const response = await axios.get(`${API}/admin/organisation`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        const features = response.data.subscription_features || {};
        setSubscriptionFeatures(features);
        
        // If taskManagement feature is not available, redirect to plans (safe landing page)
        if (!features.taskManagement) {
          navigate('/admin/plans', { replace: true });
        }
      } catch (error) {
        console.error('Failed to check subscription:', error);
      } finally {
        setIsChecking(false);
      }
    };
    
    checkAccess();
  }, [navigate]);

  if (isChecking) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-600"></div>
        <span className="ml-3 text-gray-600">Checking access...</span>
      </div>
    );
  }

  if (subscriptionFeatures && !subscriptionFeatures.taskManagement) {
    return null; // Will be redirected
  }

  return <TaskLists />;
};

const AdminSitesPage = () => {
  const { sites = [], fetchData = () => {} } = useOutletContext();
  const navigate = useNavigate();
  const [subscriptionFeatures, setSubscriptionFeatures] = React.useState(null);
  const [isChecking, setIsChecking] = React.useState(true);

  React.useEffect(() => {
    const checkAccess = async () => {
      try {
        const response = await axios.get(`${API}/admin/organisation`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        const features = response.data.subscription_features || {};
        setSubscriptionFeatures(features);
        
        // If siteManagement feature is not available, redirect to plans (safe landing page)
        if (!features.siteManagement) {
          navigate('/admin/plans', { replace: true });
        }
      } catch (error) {
        console.error('Failed to check subscription:', error);
      } finally {
        setIsChecking(false);
      }
    };
    
    checkAccess();
  }, [navigate]);

  if (isChecking) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-600"></div>
        <span className="ml-3 text-gray-600">Checking access...</span>
      </div>
    );
  }

  if (subscriptionFeatures && !subscriptionFeatures.siteManagement) {
    return null; // Will be redirected
  }

  return <SiteManagement sites={sites} fetchSites={fetchData} />;
};

const AdminSiteAssignerPage = () => {
  const navigate = useNavigate();
  const [subscriptionFeatures, setSubscriptionFeatures] = React.useState(null);
  const [isChecking, setIsChecking] = React.useState(true);

  React.useEffect(() => {
    const checkAccess = async () => {
      try {
        const response = await axios.get(`${API}/admin/organisation`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        const features = response.data.subscription_features || {};
        setSubscriptionFeatures(features);
        
        // If siteManagement feature is not available, redirect to plans (safe landing page)
        if (!features.siteManagement) {
          navigate('/admin/plans', { replace: true });
        }
      } catch (error) {
        console.error('Failed to check subscription:', error);
      } finally {
        setIsChecking(false);
      }
    };
    
    checkAccess();
  }, [navigate]);

  if (isChecking) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-600"></div>
        <span className="ml-3 text-gray-600">Checking access...</span>
      </div>
    );
  }

  if (subscriptionFeatures && !subscriptionFeatures.siteManagement) {
    return null; // Will be redirected
  }

  return <SiteAssigner />;
};

const AdminTeamDashboardSettingsPage = () => {
  return <TeamDashboardSettings />;
};


const AdminTeamCapacityPage = () => {
  const navigate = useNavigate();
  const [subscriptionFeatures, setSubscriptionFeatures] = React.useState(null);
  const [isChecking, setIsChecking] = React.useState(true);

  React.useEffect(() => {
    const checkAccess = async () => {
      try {
        const response = await axios.get(`${API}/admin/organisation`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        const features = response.data.subscription_features || {};
        setSubscriptionFeatures(features);
        
        // If teamCapacity feature is not available, redirect to plans (safe landing page)
        if (!features.teamCapacity) {
          navigate('/admin/plans', { replace: true });
        }
      } catch (error) {
        console.error('Failed to check subscription:', error);
      } finally {
        setIsChecking(false);
      }
    };
    
    checkAccess();
  }, [navigate]);

  if (isChecking) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-600"></div>
        <span className="ml-3 text-gray-600">Checking access...</span>
      </div>
    );
  }

  if (subscriptionFeatures && !subscriptionFeatures.teamCapacity) {
    return null; // Will be redirected
  }

  return <TeamCapacity />;
};

const AdminReportsPage = () => {
  return <Reports />;
};

// Individual report type pages
const AdminProgressReportsPage = () => {
  const navigate = useNavigate();
  const [subscriptionFeatures, setSubscriptionFeatures] = React.useState(null);
  const [isChecking, setIsChecking] = React.useState(true);

  React.useEffect(() => {
    const checkAccess = async () => {
      try {
        const response = await axios.get(`${API}/admin/organisation`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        const features = response.data.subscription_features || {};
        setSubscriptionFeatures(features);
        
        // If progressReports feature is not available, redirect to plans (safe landing page)
        if (!features.progressReports) {
          navigate('/admin/plans', { replace: true });
        }
      } catch (error) {
        console.error('Failed to check subscription:', error);
      } finally {
        setIsChecking(false);
      }
    };
    
    checkAccess();
  }, [navigate]);

  if (isChecking) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-600"></div>
        <span className="ml-3 text-gray-600">Checking access...</span>
      </div>
    );
  }

  if (subscriptionFeatures && !subscriptionFeatures.progressReports) {
    return null; // Will be redirected
  }

  return <Reports defaultReportType="monthly-progress" />;
};

const AdminWorkDiaryPage = () => {
  const navigate = useNavigate();
  const [subscriptionFeatures, setSubscriptionFeatures] = React.useState(null);
  const [isChecking, setIsChecking] = React.useState(true);

  React.useEffect(() => {
    const checkAccess = async () => {
      try {
        const response = await axios.get(`${API}/admin/organisation`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        const features = response.data.subscription_features || {};
        setSubscriptionFeatures(features);
        
        // If workDiary feature is not available, redirect to plans (safe landing page)
        if (!features.workDiary) {
          navigate('/admin/plans', { replace: true });
        }
      } catch (error) {
        console.error('Failed to check subscription:', error);
      } finally {
        setIsChecking(false);
      }
    };
    
    checkAccess();
  }, [navigate]);

  if (isChecking) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-600"></div>
        <span className="ml-3 text-gray-600">Checking access...</span>
      </div>
    );
  }

  if (subscriptionFeatures && !subscriptionFeatures.workDiary) {
    return null; // Will be redirected
  }

  return <AdminWorkLogPage />;
};

const AdminAttendanceReportsPage = () => {
  const navigate = useNavigate();
  const [subscriptionFeatures, setSubscriptionFeatures] = React.useState(null);
  const [isChecking, setIsChecking] = React.useState(true);

  React.useEffect(() => {
    const checkAccess = async () => {
      try {
        const response = await axios.get(`${API}/admin/organisation`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        const features = response.data.subscription_features || {};
        setSubscriptionFeatures(features);
        
        // If teamPerformance feature is not available, redirect to plans (safe landing page)
        if (!features.teamPerformance) {
          navigate('/admin/plans', { replace: true });
        }
      } catch (error) {
        console.error('Failed to check subscription:', error);
      } finally {
        setIsChecking(false);
      }
    };
    
    checkAccess();
  }, [navigate]);

  if (isChecking) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-600"></div>
        <span className="ml-3 text-gray-600">Checking access...</span>
      </div>
    );
  }

  if (subscriptionFeatures && !subscriptionFeatures.teamPerformance) {
    return null; // Will be redirected
  }

  return <AdminAttendanceTaskPage />;
};

const AdminPaymentsPage = () => {
  const navigate = useNavigate();
  const [subscriptionFeatures, setSubscriptionFeatures] = React.useState(null);
  const [isChecking, setIsChecking] = React.useState(true);

  React.useEffect(() => {
    const checkAccess = async () => {
      try {
        const response = await axios.get(`${API}/admin/organisation`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        const features = response.data.subscription_features || {};
        setSubscriptionFeatures(features);
        
        // If payments feature is not available, redirect to plans (safe landing page)
        if (!features.payments) {
          navigate('/admin/plans', { replace: true });
        }
      } catch (error) {
        console.error('Failed to check subscription:', error);
      } finally {
        setIsChecking(false);
      }
    };
    
    checkAccess();
  }, [navigate]);

  if (isChecking) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-600"></div>
        <span className="ml-3 text-gray-600">Checking access...</span>
      </div>
    );
  }

  if (subscriptionFeatures && !subscriptionFeatures.payments) {
    return null; // Will be redirected
  }

  return <AdminPaymentsPageComponent />;
};

const AdminExpensesPage = () => {
  const navigate = useNavigate();
  const [subscriptionFeatures, setSubscriptionFeatures] = React.useState(null);
  const [isChecking, setIsChecking] = React.useState(true);

  React.useEffect(() => {
    const checkAccess = async () => {
      try {
        const response = await axios.get(`${API}/admin/organisation`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        const features = response.data.subscription_features || {};
        setSubscriptionFeatures(features);
        
        // If expenses feature is not available, redirect to plans (safe landing page)
        if (!features.expenses) {
          navigate('/admin/plans', { replace: true });
        }
      } catch (error) {
        console.error('Failed to check subscription:', error);
      } finally {
        setIsChecking(false);
      }
    };
    
    checkAccess();
  }, [navigate]);

  if (isChecking) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-600"></div>
        <span className="ml-3 text-gray-600">Checking access...</span>
      </div>
    );
  }

  if (subscriptionFeatures && !subscriptionFeatures.expenses) {
    return null; // Will be redirected
  }

  return <AdminExpensesPageComponent />;
};

const AdminOvertimePage = () => {
  const navigate = useNavigate();
  const [subscriptionFeatures, setSubscriptionFeatures] = React.useState(null);
  const [isChecking, setIsChecking] = React.useState(true);

  React.useEffect(() => {
    const checkAccess = async () => {
      try {
        const response = await axios.get(`${API}/admin/organisation`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        const features = response.data.subscription_features || {};
        setSubscriptionFeatures(features);
        
        // If overtime feature is not available, redirect to plans (safe landing page)
        if (!features.overtime) {
          navigate('/admin/plans', { replace: true });
        }
      } catch (error) {
        console.error('Failed to check subscription:', error);
      } finally {
        setIsChecking(false);
      }
    };
    
    checkAccess();
  }, [navigate]);

  if (isChecking) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-600"></div>
        <span className="ml-3 text-gray-600">Checking access...</span>
      </div>
    );
  }

  if (subscriptionFeatures && !subscriptionFeatures.overtime) {
    return null; // Will be redirected
  }

  return <AdminOvertimePageComponent />;
};

const AdminHolidaysPage = () => {
  const navigate = useNavigate();
  const [subscriptionFeatures, setSubscriptionFeatures] = React.useState(null);
  const [isChecking, setIsChecking] = React.useState(true);

  React.useEffect(() => {
    const checkAccess = async () => {
      try {
        const response = await axios.get(`${API}/admin/organisation`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        const features = response.data.subscription_features || {};
        setSubscriptionFeatures(features);
        
        // If holidays feature is not available, redirect to plans (safe landing page)
        if (!features.holidaysModule) {
          navigate('/admin/plans', { replace: true });
        }
      } catch (error) {
        console.error('Failed to check subscription:', error);
      } finally {
        setIsChecking(false);
      }
    };
    
    checkAccess();
  }, [navigate]);

  if (isChecking) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-600"></div>
        <span className="ml-3 text-gray-600">Checking access...</span>
      </div>
    );
  }

  if (subscriptionFeatures && !subscriptionFeatures.holidaysModule) {
    return null; // Will be redirected
  }

  return <AdminHolidaysPageComponent />;
};

const AdminInvoicesPage = () => {
  const navigate = useNavigate();
  const [subscriptionFeatures, setSubscriptionFeatures] = React.useState(null);
  const [isChecking, setIsChecking] = React.useState(true);

  React.useEffect(() => {
    const checkAccess = async () => {
      try {
        const response = await axios.get(`${API}/admin/organisation`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        const features = response.data.subscription_features || {};
        setSubscriptionFeatures(features);
        
        // If payments feature is not available (controls both Payments and Invoices), redirect to plans (safe landing page)
        if (!features.payments) {
          navigate('/admin/plans', { replace: true });
        }
      } catch (error) {
        console.error('Failed to check subscription:', error);
      } finally {
        setIsChecking(false);
      }
    };
    
    checkAccess();
  }, [navigate]);

  if (isChecking) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-600"></div>
        <span className="ml-3 text-gray-600">Checking access...</span>
      </div>
    );
  }

  if (subscriptionFeatures && !subscriptionFeatures.payments) {
    return null; // Will be redirected
  }

  return <AdminInvoicesPageComponent />;
};

const AdminPlansPage = () => {
  return <AdminPlansPageComponent />;
};

const AdminProcurementPageComponent = () => {
  const navigate = useNavigate();
  const [subscriptionFeatures, setSubscriptionFeatures] = React.useState(null);
  const [isChecking, setIsChecking] = React.useState(true);

  React.useEffect(() => {
    const checkAccess = async () => {
      try {
        const response = await axios.get(`${API}/admin/organisation`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        const features = response.data.subscription_features || {};
        setSubscriptionFeatures(features);
        
        // If procurement feature is not available, redirect to plans (safe landing page)
        if (!features.procurement) {
          navigate('/admin/plans', { replace: true });
        }
      } catch (error) {
        console.error('Failed to check subscription:', error);
      } finally {
        setIsChecking(false);
      }
    };
    
    checkAccess();
  }, [navigate]);

  if (isChecking) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-600"></div>
        <span className="ml-3 text-gray-600">Checking access...</span>
      </div>
    );
  }

  if (subscriptionFeatures && !subscriptionFeatures.procurement) {
    return null; // Will be redirected
  }

  return <AdminProcurementPage />;
};

const AdminPhotoGalleryPage = () => {
  const navigate = useNavigate();
  const [subscriptionFeatures, setSubscriptionFeatures] = React.useState(null);
  const [isChecking, setIsChecking] = React.useState(true);

  React.useEffect(() => {
    const checkAccess = async () => {
      try {
        const response = await axios.get(`${API}/admin/organisation`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        const features = response.data.subscription_features || {};
        setSubscriptionFeatures(features);
        
        // If photoGallery feature is not available, redirect to plans (safe landing page)
        if (!features.photoGallery) {
          navigate('/admin/plans', { replace: true });
        }
      } catch (error) {
        console.error('Failed to check subscription:', error);
      } finally {
        setIsChecking(false);
      }
    };
    
    checkAccess();
  }, [navigate]);

  if (isChecking) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-600"></div>
        <span className="ml-3 text-gray-600">Checking access...</span>
      </div>
    );
  }

  if (subscriptionFeatures && !subscriptionFeatures.photoGallery) {
    return null; // Will be redirected
  }

  return <PhotoGalleryPage />;
};

// Backward compatibility: Redirect hash URLs to clean URLs
const HashRedirectHandler = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const hasRedirected = useRef(false);
  
  useEffect(() => {
    // If URL contains hash routing (e.g., /#/admin/dashboard), redirect to clean URL
    if (window.location.hash.startsWith('#/') && !hasRedirected.current) {
      hasRedirected.current = true;
      const hashPath = window.location.hash.slice(1); // Remove the #
      
      // Parse hash path to extract path and query params
      const queryIndex = hashPath.indexOf('?');
      let cleanPath = hashPath;
      let queryString = '';
      
      if (queryIndex !== -1) {
        cleanPath = hashPath.substring(0, queryIndex);
        queryString = hashPath.substring(queryIndex);
      }
      
      // Use navigate for seamless client-side routing (no reload needed)
      navigate(cleanPath + queryString, { replace: true });
    }
  }, [navigate, location]);
  
  return children;
};

// Main App Component
function App() {
  return (
    <BrowserRouter>
      <HashRedirectHandler>
      <MobileTokenAuthHandler>
        <Routes>
        {/* Landing Page */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/tour" element={<TourPage />} />

        {/* Registration route */}
        <Route path="/register" element={<OrganisationRegistration />} />
        
        {/* Login Routes */}
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/super-admin/login" element={<SuperAdminLogin />} />
        
        {/* Password Reset Routes */}
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
        
        {/* Legal Pages Routes (Public) */}
        <Route path="/terms-of-service" element={<TermsOfServicePage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
        
        {/* Support Pages Routes (Public) */}
        <Route path="/faqs" element={<FAQsPage />} />
        <Route path="/account-deletion" element={<AccountDeletionPage />} />
        
        {/* Team Portal Routes */}
        <Route path="/team/login" element={<TeamLoginPage />} />
        <Route path="/team/dashboard" element={<TeamDashboardPage />} />
        
        {/* Admin Dashboard Routes */}
        <Route path="/admin/*" element={
          <ProtectedAdminRoute>
            <AdminLayout />
          </ProtectedAdminRoute>
        }>
          <Route path="dashboard" element={<AdminDashboardPage />} />
          <Route path="users" element={<AdminUsersPage />} />
          <Route path="tasks" element={<AdminTasksPage />} />
          <Route path="team-capacity" element={<AdminTeamCapacityPage />} />
          <Route path="sites" element={<AdminSitesPage />} />
          <Route path="site-assigner" element={<AdminSiteAssignerPage />} />
          <Route path="procurement" element={<AdminProcurementPageComponent />} />
          <Route path="team-dashboard-settings" element={<AdminTeamDashboardSettingsPage />} />
          <Route path="reports" element={<AdminReportsPage />} />
          <Route path="reports/progress" element={<AdminProgressReportsPage />} />
          <Route path="reports/work-diary" element={<AdminWorkDiaryPage />} />
          <Route path="reports/attendance" element={<AdminAttendanceReportsPage />} />
          <Route path="payments" element={<AdminPaymentsPage />} />
          <Route path="expenses" element={<AdminExpensesPage />} />
          <Route path="invoices" element={<AdminInvoicesPage />} />
          <Route path="plans" element={<AdminPlansPage />} />
          <Route path="user-preferences" element={<UserPreferencesPage />} />
          <Route path="overtime" element={<AdminOvertimePage />} />
          <Route path="holidays" element={<AdminHolidaysPage />} />
          <Route path="photo-gallery" element={<AdminPhotoGalleryPage />} />
          <Route path="neo-credits" element={<NeoCreditPurchasePage />} />
          <Route path="quoting" element={<AdminQuotingPage />} />
          <Route path="clients" element={<AdminClientsPage />} />
        </Route>
        
        {/* Super Admin Dashboard Routes */}
        <Route path="/super-admin/*" element={
          <ProtectedSuperAdminRoute>
            <SuperAdminLayout />
          </ProtectedSuperAdminRoute>
        }>
          <Route path="dashboard" element={<SuperAdminDashboardPage />} />
          <Route path="organisations" element={<OrganisationsPage />} />
          <Route path="organisation-data" element={<OrganisationDataPage />} />
          <Route path="organisation-analytics" element={<OrganisationAnalyticsPage />} />
          <Route path="explore-hub" element={<ExploreHubPage />} />
          <Route path="paypal-setup" element={<PayPalSetupPage />} />
          <Route path="subscription-packages" element={<SubscriptionPackagesPage />} />
          <Route path="flash-upgrades" element={<FlashUpgradesPage />} />
          <Route path="business-analytics" element={<FinancialsPage />} />
          <Route path="subscriptions-analytics" element={<SubscriptionsPage />} />

          <Route path="financial-health" element={<FinancialHealthPage />} />

          <Route path="geography-demographics" element={<GeographyDemographicsPage />} />

          <Route path="time-based-insights" element={<TimeBasedInsightsPage />} />

          <Route path="settings/users" element={<SettingsUsersPage />} />
          <Route path="neo-settings" element={<NeoSettingsPage />} />
          <Route path="neo-analytics" element={<NeoAnalyticsPage />} />
          <Route path="neo-history" element={<NeoHistoryPage />} />
          <Route path="neo-feedback" element={<NeoFeedbackPage />} />
          <Route path="help/tickets" element={<TicketsPage />} />
          <Route path="help/broadcast" element={<BroadcastPage />} />
          <Route path="tools-reports" element={<ToolsReportsPage />} />
          <Route path="app-metadata" element={<AppMetadataPage />} />
          <Route path="feedback-bugs" element={<FeedbackBugsPage />} />
          <Route path="app-analytics" element={<AppAnalyticsPage />} />
          <Route path="app-analytics/performance-technical" element={<PerformanceTechnicalPage />} />
          <Route path="app-analytics/retention-lifecycle" element={<RetentionLifecyclePage />} />

          <Route path="app-analytics/user-behaviour" element={<UserBehaviourPage />} />

          <Route path="app-analytics/feature-usage" element={<FeatureUsagePage />} />

          <Route path="app-analytics/user-engagement" element={<UserEngagementPage />} />
          <Route path="app-analytics/explore-analytics" element={<ExploreAnalyticsPage />} />
          <Route path="deployment-tools" element={<DeploymentToolsPage />} />
          <Route path="paypal-events" element={<PayPalEventsPage />} />
          <Route path="platform-logs" element={<PlatformLogsPage />} />
          <Route path="system-logs" element={<SystemLogsPage />} />
          <Route path="marketing-campaigns" element={<MarketingCampaignsPage />} />
          <Route path="faq-management" element={<FAQManagementPage />} />

          <Route path="admin-portal/error-support" element={<ErrorSupportPage />} />

          <Route path="admin-portal/access-security" element={<AccessSecurityPage />} />

          <Route path="admin-portal/workflow-productivity" element={<WorkflowProductivityPage />} />

          <Route path="admin-portal/usage-adoption" element={<UsageFeatureAdoptionPage />} />

          <Route path="admin-portal-analytics" element={<AdminPortalAnalyticsPage />} />
          <Route path="notification-analytics" element={<NotificationAnalyticsPage />} />
        </Route>
      </Routes>
      </MobileTokenAuthHandler>
      </HashRedirectHandler>
    </BrowserRouter>
  );
}

export default App;